import React, { useState, useEffect, useCallback } from 'react';
import { assetService, bookingService } from '../services/api';
import { clientCache } from '../services/clientCache';
import './AdminAssets.css';

export default function AdminAssets({ assets, allBookings, onAssetsUpdate, onBookingsRefreshNeeded }) {
  const [localAssets, setLocalAssets] = useState(assets || []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  
  const [addForm, setAddForm] = useState({ name: '', type: 'wing', available: true });
  const [addMsg, setAddMsg] = useState('');
  const [addMsgType, setAddMsgType] = useState('success'); // 'success' or 'error'

  const [editForm, setEditForm] = useState({ id: '', name: '', type: 'wing', available: true });
  const [editMsg, setEditMsg] = useState('');
  const [editMsgType, setEditMsgType] = useState('success'); // 'success' or 'error'

  // Asset details modal state
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showAssetDetails, setShowAssetDetails] = useState(false);
  const [pastBookingsLimit, setPastBookingsLimit] = useState(5); // Start with 5 past bookings shown
  const [assetHistoryById, setAssetHistoryById] = useState({});
  const [assetHistoryLoading, setAssetHistoryLoading] = useState(false);

  const fetchAssets = useCallback(async () => {
    try {
      setLoading(true);
      const assetsData = await assetService.getAll();
      // Ensure assets is always an array
      const fetchedAssets = Array.isArray(assetsData?.data) ? assetsData.data : [];
      setLocalAssets(fetchedAssets);
      
      // Update centralized state if callback is provided
      if (onAssetsUpdate) {
        onAssetsUpdate(fetchedAssets);
      }
    } catch (err) {
      setError(err.error || err.message || 'Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, [onAssetsUpdate]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  // Update local assets when props change
  useEffect(() => {
    if (assets) {
      setLocalAssets(assets);
    }
  }, [assets]);

  const handleAddChange = e => {
    const { name, value, type, checked } = e.target;
    setAddForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleAdd = async e => {
    e.preventDefault();
    setAddMsg('');
    setAddMsgType('success');
    try {
      const response = await assetService.create(addForm);
      setAddMsg('Asset created successfully');
      setAddMsgType('success');
      setAddForm({ name: '', type: 'wing', available: true });
      
      // Add the new asset to both local and centralized state immediately
      const newAsset = response.data;
      const updatedAssets = [...localAssets, newAsset];
      setLocalAssets(updatedAssets);
      
      if (onAssetsUpdate) {
        onAssetsUpdate(updatedAssets);
      }

  // Update client-side cache so other screens see the latest immediately
  try { await clientCache.cacheAssets(updatedAssets, Date.now()); } catch {}
    } catch (err) {
      setAddMsg(err.error || err.message || 'Failed to create asset');
      setAddMsgType('error');
    }
  };

  const handleEdit = async e => {
    e.preventDefault();
    setEditMsg('');
    setEditMsgType('success');
    try {
      const response = await assetService.update(editForm.id, {
        name: editForm.name,
        type: editForm.type,
        available: editForm.available
      });
      
      // Check if there's a special message about affected bookings
      if (response.message) {
        setEditMsg(response.message);
        setEditMsgType('success');
        // If bookings were affected, trigger a refresh
        if (onBookingsRefreshNeeded && response.message.includes('bookings were affected')) {
          onBookingsRefreshNeeded();
        }
      } else if (response.warning) {
        setEditMsg(`Asset updated. Warning: ${response.warning}`);
        setEditMsgType('error'); // Warnings should be displayed as errors (red)
        // If there was a warning about bookings, might want to refresh anyway
        if (onBookingsRefreshNeeded && response.warning.includes('affected bookings')) {
          onBookingsRefreshNeeded();
        }
      } else {
        setEditMsg('Asset updated successfully');
        setEditMsgType('success');
      }
      
      // Update the asset in both local and centralized state immediately
      const updatedAsset = response.data;
      const updatedAssets = localAssets.map(asset => 
        asset._id === editForm.id ? updatedAsset : asset
      );
      setLocalAssets(updatedAssets);
      
      if (onAssetsUpdate) {
        onAssetsUpdate(updatedAssets);
      }

  // Update client-side cache so navigation back uses fresh data
  try { await clientCache.cacheAssets(updatedAssets, Date.now()); } catch {}
      
      setEditForm({ id: '', name: '', type: 'wing', available: true });
    } catch (err) {
      setEditMsg(err.error || err.message || 'Failed to update asset');
      setEditMsgType('error');
    }
  };

  const handleRemove = async id => {
    try {
      setSuccessMsg('');
      setError(null);
      
      const response = await assetService.delete(id);
      
      // Remove the asset from both local and centralized state immediately
      const updatedAssets = localAssets.filter(asset => asset._id !== id);
      setLocalAssets(updatedAssets);
      
      if (onAssetsUpdate) {
        onAssetsUpdate(updatedAssets);
      }

  // Update client-side cache so other screens see deletion immediately
  try { await clientCache.cacheAssets(updatedAssets, Date.now()); } catch {}
      
      // Show feedback about affected bookings if any
      if (response.message) {
        setSuccessMsg(`Asset deleted. ${response.message}`);
        // If bookings were affected, trigger a refresh
        if (onBookingsRefreshNeeded && response.message.includes('bookings were affected')) {
          onBookingsRefreshNeeded();
        }
      } else if (response.warning) {
        setError(`Asset deleted. Warning: ${response.warning}`); // Warnings should be errors (red)
        // If there was a warning about bookings, might want to refresh anyway
        if (onBookingsRefreshNeeded && response.warning.includes('affected bookings')) {
          onBookingsRefreshNeeded();
        }
      } else {
        setSuccessMsg('Asset deleted successfully.');
      }
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMsg(''), 5000);
    } catch (err) {
      console.error(err);
      setError(err.error || err.message || 'Failed to delete asset');
    }
  };

  // Calculate comprehensive asset statistics
  const calculateAssetStats = (assetId) => {
    const bookings = assetHistoryById[assetId] || allBookings?.[assetId] || [];

    if (!bookings || bookings.length === 0) {
      return {
        totalBookings: 0,
        pastBookings: [],
        futureBookings: [],
        bookingsByUser: {},
        allUsers: [],
        mostFrequentUser: null,
        lastBookedBy: null,
        totalUsageHours: 0,
        bookingsByMonth: {},
        bookingsByDayOfWeek: {},
        currentlyBooked: false,
        upcomingBooking: null
      };
    }
    const now = new Date();
    
    const pastBookings = bookings.filter(b => new Date(b.end) < now);
    const futureBookings = bookings.filter(b => new Date(b.start) > now);
    const currentBookings = bookings.filter(b => 
      new Date(b.start) <= now && new Date(b.end) > now
    );

    // Group by user
    const bookingsByUser = {};
    bookings.forEach(booking => {
      const userName = booking.name || booking.title || 'Unknown User';
      if (!bookingsByUser[userName]) {
        bookingsByUser[userName] = {
          count: 0,
          totalHours: 0,
          bookings: []
        };
      }
      bookingsByUser[userName].count++;
      bookingsByUser[userName].bookings.push(booking);
      
      const duration = (new Date(booking.end) - new Date(booking.start)) / (1000 * 60 * 60);
      bookingsByUser[userName].totalHours += duration;
    });

    // Find most frequent user
    const mostFrequentUser = Object.entries(bookingsByUser)
      .sort((a, b) => b[1].count - a[1].count)[0];

    // Find last person to book
    const lastBooking = [...bookings]
      .sort((a, b) => new Date(b.start) - new Date(a.start))[0];
    const lastBookedBy = lastBooking ? (lastBooking.name || lastBooking.title || 'Unknown User') : null;

    // Get all unique users who have booked this asset
    const allUsers = Object.entries(bookingsByUser)
      .map(([name, data]) => ({
        name,
        bookingCount: data.count,
        totalHours: Math.round(data.totalHours * 10) / 10
      }))
      .sort((a, b) => b.bookingCount - a.bookingCount);

    // Calculate total usage hours
    const totalDuration = bookings.reduce((sum, booking) => {
      return sum + (new Date(booking.end) - new Date(booking.start));
    }, 0);
    const totalUsageHours = totalDuration / (1000 * 60 * 60);

    // Bookings by month
    const bookingsByMonth = {};
    bookings.forEach(booking => {
      const month = new Date(booking.start).toLocaleString('default', { month: 'long', year: 'numeric' });
      bookingsByMonth[month] = (bookingsByMonth[month] || 0) + 1;
    });

    // Bookings by day of week
    const bookingsByDayOfWeek = {};
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    bookings.forEach(booking => {
      const day = days[new Date(booking.start).getDay()];
      bookingsByDayOfWeek[day] = (bookingsByDayOfWeek[day] || 0) + 1;
    });

    // Upcoming booking
    const upcomingBooking = futureBookings
      .sort((a, b) => new Date(a.start) - new Date(b.start))[0];

    return {
      totalBookings: bookings.length,
      pastBookings: pastBookings.sort((a, b) => new Date(b.start) - new Date(a.start)),
      futureBookings: futureBookings.sort((a, b) => new Date(a.start) - new Date(b.start)),
      bookingsByUser,
      mostFrequentUser: mostFrequentUser ? {
        name: mostFrequentUser[0],
        count: mostFrequentUser[1].count,
        totalHours: mostFrequentUser[1].totalHours
      } : null,
      lastBookedBy,
      allUsers,
      totalUsageHours,
      bookingsByMonth,
      bookingsByDayOfWeek,
      currentlyBooked: currentBookings.length > 0,
      currentBooking: currentBookings[0] || null,
      upcomingBooking
    };
  };

  // Handle asset click to show details
  const handleAssetClick = async (asset) => {
    setSelectedAsset(asset);
    setShowAssetDetails(true);
    setPastBookingsLimit(5);

    if (assetHistoryById[asset._id]) {
      return;
    }

    setAssetHistoryLoading(true);
    try {
      const response = await bookingService.getByAsset(asset._id);
      setAssetHistoryById(prev => ({
        ...prev,
        [asset._id]: Array.isArray(response.data) ? response.data : []
      }));
    } catch (err) {
      console.error('Failed to load asset booking history:', err);
      setAssetHistoryById(prev => ({
        ...prev,
        [asset._id]: allBookings?.[asset._id] || []
      }));
    } finally {
      setAssetHistoryLoading(false);
    }
  };

  // Close asset details modal
  const closeAssetDetails = () => {
    setShowAssetDetails(false);
    setSelectedAsset(null);
    setPastBookingsLimit(5); // Reset to initial limit when closing modal
    setAssetHistoryLoading(false);
  };

  // Load more past bookings
  const loadMorePastBookings = () => {
    setPastBookingsLimit(prev => prev + 10);
  };

  if (loading) return <div>Loading assets...</div>;

  return (
    <div className="admin-assets-container">
      <h2>Asset Management</h2>

      {/* Add Asset Form - moved above the table */}
      <div className="admin-assets-forms">
        <form onSubmit={handleAdd} className="asset-form">
          <h3>Add Asset</h3>
          <input name="name" placeholder="Name" value={addForm.name} onChange={handleAddChange} required />
          <select name="type" value={addForm.type} onChange={handleAddChange}>
            <option value="wing">wing</option>
            <option value="foil">foil</option>
            <option value="board">board</option>
          </select>
          <label>
            <input type="checkbox" name="available" checked={addForm.available} onChange={handleAddChange} /> Available
          </label>
          <button type="submit">Create</button>
          {addMsg && <div className={`form-message ${addMsgType === 'success' ? 'success' : ''}`}>{addMsg}</div>}
        </form>
      </div>

      {error && <div className="error">{error}</div>}
      {successMsg && <div className="form-message success">{successMsg}</div>}
      <table className="admin-assets-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Available</th>
          </tr>
        </thead>
        <tbody>
          {localAssets.map(a => (
            <tr 
              key={a._id} 
              className="clickable-row" 
              onClick={() => handleAssetClick(a)}
              title="Click to view asset details"
            >
              <td>{a.name}</td>
              <td>{a.type}</td>
              <td>{a.available ? 'Yes' : 'No'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Asset Details Modal */}
      {showAssetDetails && selectedAsset && (
        <div className="asset-details-modal-overlay">
          <div className="asset-details-modal">
            <div className="asset-details-header">
              <h2>{selectedAsset.name} - Asset Details</h2>
              <button className="close-modal-btn" onClick={closeAssetDetails}>×</button>
            </div>
            
            <div className="asset-details-content">
              {assetHistoryLoading && !assetHistoryById[selectedAsset._id] ? (
                <div>Loading booking history...</div>
              ) : (() => {
                const stats = calculateAssetStats(selectedAsset._id);
                
                return (
                  <>
                    {/* Editable Asset Info Section */}
                    <div className="asset-info-section">
                      <h3>Asset Information</h3>
                      <div className="asset-info-grid" style={{display: 'flex', flexDirection: 'column', gap: '0'}}>
                        {/* Name */}
                        <div className="info-item" style={{display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%', gap: '16px'}}>
                          <label>Name:</label>
                          <div className="info-value" style={{flex: '1 1 0', minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden'}}>
                            {editForm.id === selectedAsset._id && editForm.field === 'name' ? (
                              <>
                                <input
                                  type="text"
                                  value={editForm.name}
                                  onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                                  required
                                  className="edit-input"
                                />
                              </>
                            ) : (
                              <span>{selectedAsset.name}</span>
                            )}
                          </div>
                          <div className="button-group">
                            {editForm.id === selectedAsset._id && editForm.field === 'name' ? (
                              <>
                                <button onClick={async e => {
                                  e.preventDefault(); 
                                  await handleEdit({preventDefault:()=>{}});
                                  // Update selectedAsset immediately for instant UI feedback
                                  setSelectedAsset(prev => prev ? { ...prev, name: editForm.name } : prev);
                                  setEditForm({ id: '', name: '', type: 'wing', available: true });
                                }} className="save-btn">Save</button>
                                <button type="button" className="cancel-btn" onClick={() => setEditForm({ id: '', name: '', type: 'wing', available: true })}>Cancel</button>
                              </>
                            ) : (
                              <button onClick={() => setEditForm({ id: selectedAsset._id, name: selectedAsset.name, type: selectedAsset.type, available: selectedAsset.available, field: 'name' })} className="edit-btn">Edit</button>
                            )}
                          </div>
                        </div>
                        {/* Type */}
                        <div className="info-item" style={{display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%', gap: '16px'}}>
                          <label>Type:</label>
                          <div className="info-value" style={{flex: '1 1 0', minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden'}}>
                            {editForm.id === selectedAsset._id && editForm.field === 'type' ? (
                              <>
                                <select
                                  value={editForm.type}
                                  onChange={e => setEditForm(prev => ({ ...prev, type: e.target.value }))}
                                  required
                                  className="edit-select"
                                >
                                  <option value="wing">Wing</option>
                                  <option value="foil">Foil</option>
                                  <option value="board">Board</option>
                                </select>
                              </>
                            ) : (
                              <span className="asset-type-badge">{selectedAsset.type}</span>
                            )}
                          </div>
                          <div className="button-group">
                            {editForm.id === selectedAsset._id && editForm.field === 'type' ? (
                              <>
                                <button onClick={async e => {
                                  e.preventDefault(); 
                                  await handleEdit({preventDefault:()=>{}});
                                  // Update selectedAsset immediately for instant UI feedback
                                  setSelectedAsset(prev => prev ? { ...prev, type: editForm.type } : prev);
                                  setEditForm({ id: '', name: '', type: 'wing', available: true });
                                }} className="save-btn">Save</button>
                                <button type="button" className="cancel-btn" onClick={() => setEditForm({ id: '', name: '', type: 'wing', available: true })}>Cancel</button>
                              </>
                            ) : (
                              <button onClick={() => setEditForm({ id: selectedAsset._id, name: selectedAsset.name, type: selectedAsset.type, available: selectedAsset.available, field: 'type' })} className="edit-btn">Edit</button>
                            )}
                          </div>
                        </div>
                        {/* Availability */}
                        <div className="info-item" style={{display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%', gap: '16px'}}>
                          <label>Status:</label>
                          <div className="info-value" style={{flex: '1 1 0', minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden'}}>
                            {editForm.id === selectedAsset._id && editForm.field === 'available' ? (
                              <>
                                <select
                                  value={editForm.available ? 'available' : 'unavailable'}
                                  onChange={e => setEditForm(prev => ({ ...prev, available: e.target.value === 'available' }))}
                                  className="edit-select"
                                >
                                  <option value="available">Available</option>
                                  <option value="unavailable">Unavailable</option>
                                </select>
                              </>
                            ) : (
                              <span className={`availability-badge ${selectedAsset.available ? 'available' : 'unavailable'}`}>
                                {selectedAsset.available ? 'Available' : 'Unavailable'}
                              </span>
                            )}
                          </div>
                          <div className="button-group">
                            {editForm.id === selectedAsset._id && editForm.field === 'available' ? (
                              <>
                                <button onClick={async e => {
                                  e.preventDefault(); 
                                  await handleEdit({preventDefault:()=>{}});
                                  // Update selectedAsset immediately for instant UI feedback
                                  setSelectedAsset(prev => prev ? { ...prev, available: editForm.available } : prev);
                                  setEditForm({ id: '', name: '', type: 'wing', available: true });
                                }} className="save-btn">Save</button>
                                <button type="button" className="cancel-btn" onClick={() => setEditForm({ id: '', name: '', type: 'wing', available: true })}>Cancel</button>
                              </>
                            ) : (
                              <button onClick={() => setEditForm({ id: selectedAsset._id, name: selectedAsset.name, type: selectedAsset.type, available: selectedAsset.available, field: 'available' })} className="edit-btn">Edit</button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Inline save/update feedback */}
                      {editMsg && (
                        <div className={`form-message ${editMsgType === 'success' ? 'success' : 'error'}`} style={{ marginTop: 12 }}>
                          {editMsg}
                        </div>
                      )}
                      {/* Remove asset button */}
                      <div className="remove-user-section">
                        <button className="remove-user-btn" onClick={() => { closeAssetDetails(); handleRemove(selectedAsset._id); }}>Remove Asset</button>
                      </div>
                    </div>

                    {/* Quick Stats */}
                    <div className="stats-section">
                      <h3>Quick Statistics</h3>
                      <div className="stats-grid">
                        <div className="stat-card">
                          <div className="stat-number">{stats.totalBookings}</div>
                          <div className="stat-label">Total Bookings</div>
                        </div>
                        <div className="stat-card">
                          <div className="stat-number">{Math.round(stats.totalUsageHours)}</div>
                          <div className="stat-label">Total Hours</div>
                        </div>
                        <div className="stat-card">
                          <div className="stat-number">{stats.futureBookings.length}</div>
                          <div className="stat-label">Upcoming Bookings</div>
                        </div>
                      </div>
                    </div>

                    {/* User Info */}
                    {(stats.mostFrequentUser || stats.lastBookedBy) && (
                      <div className="user-info-section">
                        <h3>User Information</h3>
                        <div className="user-info-grid">
                          {stats.mostFrequentUser && (
                            <div className="user-info-item">
                              <label>Most Frequent User:</label>
                              <span className="user-name">
                                {stats.mostFrequentUser.name} 
                                <small>({stats.mostFrequentUser.count} bookings, {Math.round(stats.mostFrequentUser.totalHours)} hrs)</small>
                              </span>
                            </div>
                          )}
                          {stats.lastBookedBy && (
                            <div className="user-info-item">
                              <label>Last Booked By:</label>
                              <span className="user-name">{stats.lastBookedBy}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* All Users Who Have Booked This Asset */}
                    {stats.allUsers && stats.allUsers.length > 0 && (
                      <div className="all-users-section">
                        <h3>All Users ({stats.allUsers.length})</h3>
                        <div className="users-list">
                          {stats.allUsers.map((user, index) => (
                            <div key={index} className="user-item">
                              <div className="user-name">{user.name}</div>
                              <div className="user-stats">
                                <span className="booking-count">{user.bookingCount} booking{user.bookingCount !== 1 ? 's' : ''}</span>
                                <span className="total-hours">{user.totalHours}h total</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Past Bookings */}
                    <div className="recent-bookings-section">
                      <h3>Past Bookings</h3>
                      
                      {stats.pastBookings.length > 0 ? (
                        <>
                          {/* Load More Past Bookings Link */}
                          {stats.pastBookings.length > pastBookingsLimit && (
                            <div className="load-past-bookings">
                              <button type="button" onClick={loadMorePastBookings}>
                                Load More Past Bookings ({pastBookingsLimit} of {stats.pastBookings.length} shown)
                              </button>
                            </div>
                          )}

                          <div className="bookings-list">
                            {stats.pastBookings.slice(0, pastBookingsLimit).map((booking, index) => (
                              <div key={booking.id || index} className="booking-item">
                                <div className="booking-user">{booking.name || booking.title}</div>
                                <div className="booking-date">
                                  {new Date(booking.start).toLocaleDateString()} 
                                  <span className="booking-time">
                                    {new Date(booking.start).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - 
                                    {new Date(booking.end).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                  </span>
                                </div>
                                <div className="booking-duration">
                                  {Math.round((new Date(booking.end) - new Date(booking.start)) / (1000 * 60 * 60) * 10) / 10}h
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="no-bookings-message">
                          <p>No past bookings for this asset.</p>
                        </div>
                      )}
                    </div>

                    {/* Future Bookings */}
                    {stats.futureBookings.length > 0 && (
                      <div className="future-bookings-section">
                        <h3>Upcoming Bookings</h3>
                        <div className="bookings-list">
                          {stats.futureBookings.slice(0, 10).map((booking, index) => (
                            <div key={booking.id || index} className="booking-item future">
                              <div className="booking-user">{booking.name || booking.title}</div>
                              <div className="booking-date">
                                {new Date(booking.start).toLocaleDateString()} 
                                <span className="booking-time">
                                  {new Date(booking.start).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - 
                                  {new Date(booking.end).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </span>
                              </div>
                              <div className="booking-duration">
                                {Math.round((new Date(booking.end) - new Date(booking.start)) / (1000 * 60 * 60) * 10) / 10}h
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Usage Patterns */}
                    {stats.totalBookings > 0 && (
                      <div className="usage-patterns-section">
                        <h3>Usage Patterns</h3>
                        <div className="patterns-grid">
                          <div className="pattern-card">
                            <h4>By Day of Week</h4>
                            <div className="pattern-list">
                              {Object.entries(stats.bookingsByDayOfWeek)
                                .sort((a, b) => b[1] - a[1])
                                .map(([day, count]) => (
                                  <div key={day} className="pattern-item">
                                    <span>{day}</span>
                                    <span className="pattern-count">{count}</span>
                                  </div>
                                ))}
                            </div>
                          </div>
                          <div className="pattern-card">
                            <h4>By Month</h4>
                            <div className="pattern-list">
                              {Object.entries(stats.bookingsByMonth)
                                .sort((a, b) => b[1] - a[1])
                                .slice(0, 6)
                                .map(([month, count]) => (
                                  <div key={month} className="pattern-item">
                                    <span>{month}</span>
                                    <span className="pattern-count">{count}</span>
                                  </div>
                                ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {stats.totalBookings === 0 && (
                      <div className="no-bookings-message">
                        <h3>📊 No Booking History</h3>
                        <p>This asset has not been booked yet.</p>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
